import React, { FC, useEffect, useState } from 'react';
import { DEFAULT_CONTEXT_ID } from '../../../utils/UUID';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchIndicatorById,
  updateIndicator,
} from '../../../store/landscapeAPI/indicator/reducers/indicator.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { IndicatorResponseDto } from '../../../store/landscapeAPI/indicator/dto/response/indicator-response.dto';
import ReportingDetailsIndicatorsEditTemplate from '../../templates/ReportingDetailsIndicatorsEditTemplate/ReportingDetailsIndicatorsEditTemplate';
import { IndicatorUpdateRequestDto } from '../../../store/landscapeAPI/indicator/dto/request/indicator-update-request.dto';
import { showError, showSuccess } from '../../../utils/Notifications';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';

interface ReportingDetailsIndicatorsEditPageProps {}

const ReportingDetailsIndicatorsEditPage: FC<
  ReportingDetailsIndicatorsEditPageProps
> = () => {
  const [indicator, setIndicator] = useState<IndicatorResponseDto>();

  const { id } = useParams();

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const handleOnChange = (indicator: IndicatorResponseDto) => {
    setIndicator(indicator);
  };

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!indicator) return;
    const params: TranslateParams<IndicatorResponseDto> = {
      obj: indicator,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as IndicatorResponseDto;

    const updateRequest: IndicatorUpdateRequestDto = {
      goalId: DEFAULT_CONTEXT_ID,
      compactId: indicator.compactId,
      name: translatedResponse.name,
      unit: indicator.unit ?? '',
      metricType: indicator.metricType ?? '',
      id: indicator.id,
      published: indicator.published,
    };

    await dispatch(
      updateIndicator({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  const {
    indicator: selector,
    status,
    error,
  } = useSelector((state: RootState) => state.indicatorSlice);

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchIndicatorById({ id: id, language: selectedLanguage }));
    }
  }, [id, dispatch, selectedLanguage]);

  useEffect(() => {
    if (status === 'succeeded' && selector) {
      setIndicator(selector);
    }
  }, [selector, status]);

  const handleEditIndicator = async (indicator: IndicatorResponseDto) => {
    const data: IndicatorUpdateRequestDto = {
      compactId: indicator.compactId,
      goalId: DEFAULT_CONTEXT_ID,
      id: indicator.id,
      metricType: indicator.metricType || '',
      name: indicator.name,
      published: indicator.published,
      unit: indicator.unit || '',
    };

    await dispatch(
      updateIndicator({
        updateRequest: data,
        language: selectedLanguage,
      })
    );

    showSuccess('Indicator saved successfully.');
    navigate('/reporting-details/indicators');
  };

  if (status === 'loading' || translateStatus === 'loading') {
    return <Spinner />;
  }

  if (status === 'failed') {
    showError(error || 'Indicator update failed!');
  }

  return (
    <>
      {indicator && (
        <ReportingDetailsIndicatorsEditTemplate
          data={indicator}
          handleEditIndicator={handleEditIndicator}
          handleOnChange={handleOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </>
  );
};

export default ReportingDetailsIndicatorsEditPage;
